import type { ReadonlySignal } from "@preact/signals";
import type { FormStore } from "@formisch/preact";
import type { FormEmbedField } from "../../types.js";
import type { FieldProps } from "./shared.js";

import { TextInput } from "./text-input.js";
import { EmailInput } from "./email-input.js";
import { PhoneInput } from "./phone-input.js";
import { NumberInput } from "./number-input.js";
import { Textarea } from "./textarea.js";
import { Select } from "./select.js";
import { RadioGroup } from "./radio-group.js";
import { Checkbox } from "./checkbox.js";
import { AddressInput } from "./address-input.js";
import type { AddressPick } from "../../address-search.js";
import type { AddressVerificationRecord, MinimumAddressResolution } from "../../address-verification.js";

export { InputLabel } from "./input-label.js";
export { InputError } from "./input-error.js";
export type { AddressPick } from "../../address-search.js";

interface FieldRendererProps {
  field: FormEmbedField;
  input: ReadonlySignal<any>;
  errors: ReadonlySignal<[string, ...string[]] | null>;
  props: FieldProps;
  disabled: boolean;
  form: FormStore;
  /** Last field in the form — drives the mobile keyboard's action key. */
  isLast?: boolean;
  /** Web proxy base for address search; absent = plain text address. */
  addressSearchUrl?: string;
  onAddressPick?: (name: string, pick: AddressPick | null) => void;
  registerAutoSelect?: (name: string, autoSelect: (() => boolean) | null) => void;
  minimumAddressResolution?: MinimumAddressResolution;
  registerVerify?: (name: string, verify: (() => Promise<boolean>) | null) => void;
  onAddressRecord?: (name: string, record: AddressVerificationRecord | null) => void;
}

export function FieldRenderer({
  field,
  input,
  errors,
  props,
  disabled,
  form,
  isLast = false,
  addressSearchUrl,
  onAddressPick,
  registerAutoSelect,
  minimumAddressResolution,
  registerVerify,
  onAddressRecord,
}: FieldRendererProps) {
  const common = { field, input, errors, props, disabled, isLast };

  switch (field.type) {
    case "email":
      return <EmailInput {...common} />;
    case "phone":
      return <PhoneInput {...common} form={form} />;
    case "number":
      return <NumberInput {...common} />;
    case "textarea":
      return <Textarea {...common} />;
    case "select":
      return <Select {...common} />;
    case "radio":
      return <RadioGroup {...common} form={form} />;
    case "pillCheckbox":
    case "checkbox":
      return <Checkbox {...common} form={form} />;
    case "address":
      return <AddressInput {...common} form={form} searchUrl={addressSearchUrl} onPick={onAddressPick} registerAutoSelect={registerAutoSelect}
          minimumAddressResolution={minimumAddressResolution}
          registerVerify={registerVerify}
          onRecord={onAddressRecord}
        />;
    default:
      return <TextInput {...common} />;
  }
}
